/**
 * Master Component List (Schedule module)
 *
 * One wide, per-project register that collects every component / tag / activity /
 * hour in the project scope. All data columns are "fluid" (defined per project),
 * values live in JSONB server-side. Imports (XER schedules, Excel sheets) work
 * like Power Query: append new rows or merge into existing rows on a match key.
 * Every row shows the sources that contributed to it.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import ExcelJS from 'exceljs';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

// ============================================
// Constants & small helpers
// ============================================

const ACCENT = '#0f766e';
// Virtualized grid: all rows are loaded, only the visible window is rendered.
const ROW_H = 33;      // fixed row height (px) — required for spacer math
const OVERSCAN = 25;   // extra rows rendered above/below the viewport
// Pinned (sticky-left) columns: fixed widths so the sticky offsets are exact.
const CHECK_W = 36;
const PIN_W = 170;
const MAX_PINS = 4;

const BTN = {
  padding: '0.45rem 0.9rem', backgroundColor: 'white', color: '#374151',
  border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer',
  fontWeight: 500, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const BTN_PRIMARY = { ...BTN, backgroundColor: ACCENT, color: 'white', border: 'none' };
const TH = {
  padding: '0.6rem 0.6rem', textAlign: 'left', fontWeight: 600, fontSize: '0.72rem',
  textTransform: 'uppercase', letterSpacing: '0.03em', color: '#475569',
  borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc',
  position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap',
};
const TD = { padding: '0.45rem 0.6rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', color: '#334155', whiteSpace: 'nowrap', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' };
// Grid data cells: fixed height so the virtual window math stays exact
const TD_V = { ...TD, padding: '0 0.6rem', height: ROW_H, boxSizing: 'border-box' };
const INPUT = { padding: '0.45rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.85rem' };

const SOURCE_COLORS = ['#0f766e', '#b45309', '#1d4ed8', '#7c3aed', '#be185d', '#166534', '#b91c1c', '#0e7490'];
const sourceColor = (label) => {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return SOURCE_COLORS[h % SOURCE_COLORS.length];
};

const SHARED_GROUP = 'Shared';

/** Display group of a column: its single feeding source, or "Shared" when several sources feed it. */
const groupOf = (c) => {
  if (c.sources && c.sources.length > 1) return SHARED_GROUP;
  return (c.sources && c.sources[0]) || c.column_group || 'Manual';
};
const groupColor = (g) => (g === SHARED_GROUP ? '#64748b' : sourceColor(g));

/** Column title without the redundant " (source)" suffix — the source is shown in the group header row. */
const displayLabel = (c) => {
  const g = c.column_group;
  if (g && c.label.endsWith(` (${g})`)) return c.label.slice(0, c.label.length - g.length - 3);
  return c.label;
};

const fmtNum = (v) => {
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v;
};

/** Evaluate a computed column ({ op, a, b }) against a row's attrs — mirrors the backend. */
const computeFormula = (formula, attrs) => {
  if (!formula || !formula.op || !formula.a || !formula.b) return null;
  const num = (k) => {
    const n = parseFloat(String(attrs[k] ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const date = (k) => {
    const v = attrs[k];
    if (v === undefined || v === null || v === '') return null;
    const d = new Date(String(v));
    return Number.isNaN(d.getTime()) ? null : d;
  };
  switch (formula.op) {
    case 'diff': { const a = num(formula.a), b = num(formula.b); return a !== null && b !== null ? a - b : null; }
    case 'sum': { const a = num(formula.a), b = num(formula.b); return a !== null && b !== null ? a + b : null; }
    case 'ratio': { const a = num(formula.a), b = num(formula.b); return a !== null && b !== null && b !== 0 ? a / b : null; }
    case 'dateDiff': { const a = date(formula.a), b = date(formula.b); return a && b ? Math.round((a - b) / 86400000) : null; }
    default: return null;
  }
};
const FORMULA_OPS = [
  ['diff', 'A − B (numbers)'],
  ['sum', 'A + B (numbers)'],
  ['ratio', 'A ÷ B (numbers)'],
  ['dateDiff', 'A − B in days (dates)'],
];
const formulaText = (formula, columns) => {
  if (!formula) return '';
  const name = (k) => columns.find((c) => c.column_key === k)?.label || k;
  const sym = { diff: '−', sum: '+', ratio: '÷', dateDiff: '− (days)' }[formula.op] || formula.op;
  return `${name(formula.a)} ${sym} ${name(formula.b)}`;
};

/** Normalize an ExcelJS cell value into a plain string/number. */
const cellText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    if (v.richText) return v.richText.map((r) => r.text).join('');
    if (v.result !== undefined) return cellText(v.result);
    if (v.text !== undefined) return String(v.text);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (v.error) return '';
    return String(v);
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return v;
};

// ============================================
// Reusable UI bits
// ============================================

const KPICard = memo(({ title, value, subtitle, icon, color }) => (
  <div style={{
    backgroundColor: 'white', borderRadius: 12, padding: '1rem 1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: `4px solid ${color}`,
    flex: '1 1 170px', minWidth: 160,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500 }}>{title}</span>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: '0.35rem 0 0.15rem' }}>{value}</div>
    {subtitle && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</div>}
  </div>
));

const Modal = memo(({ isOpen, onClose, title, children, width = '560px', headerExtra = null }) => {
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: 12, width, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ padding: '0.9rem 1.25rem', background: 'linear-gradient(135deg, #0f172a, #134e4a)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.02rem', color: 'white' }}>{title}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {headerExtra}
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#cbd5e1', lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>,
    document.body
  );
});

const Toast = memo(({ message, type = 'success', onClose }) => {
  const colors = {
    success: { bg: '#d1fae5', color: '#065f46', icon: '✓' },
    error: { bg: '#fee2e2', color: '#991b1b', icon: '✕' },
    info: { bg: '#dbeafe', color: '#1e40af', icon: 'ℹ' },
  };
  const s = colors[type] || colors.info;
  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, backgroundColor: s.bg, color: s.color, padding: '0.8rem 1.2rem', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 10, zIndex: 10001, maxWidth: 420 }}>
      <span>{s.icon}</span><span style={{ fontSize: '0.88rem' }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: s.color, cursor: 'pointer', fontSize: '1.1rem' }}>×</button>
    </div>
  );
});

const FilterIcon = memo(({ active }) => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill={active ? ACCENT : '#94a3b8'}>
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z" />
  </svg>
));

/** Funnel filter dropdown fed by the server distinct endpoint. */
const FilterDropdown = memo(({ values, selected, onToggle, onClear, onClose, position, loading }) => {
  const [search, setSearch] = useState('');
  const filtered = values.filter((v) => (v.value || '(blank)').toLowerCase().includes(search.toLowerCase()));
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: Math.min(position.top, window.innerHeight - 340), left: Math.min(position.left, window.innerWidth - 260), backgroundColor: 'white', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.18)', width: 250, maxHeight: 330, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '0.6rem', borderBottom: '1px solid #e5e7eb' }}>
          <input type="text" placeholder="Search values..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...INPUT, width: '100%', boxSizing: 'border-box' }} autoFocus />
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '0.4rem' }}>
          {loading ? <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</div> :
            filtered.map((v) => (
              <label key={v.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.35rem 0.5rem', cursor: 'pointer', borderRadius: 4, fontSize: '0.85rem' }}>
                <input type="checkbox" checked={selected.includes(v.value)} onChange={() => onToggle(v.value)} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.value || '(blank)'}</span>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{v.count}</span>
              </label>
            ))}
        </div>
        <div style={{ padding: '0.5rem 0.6rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onClear} style={{ ...BTN, padding: '0.25rem 0.6rem', fontSize: '0.78rem', color: '#b91c1c' }}>Clear</button>
          <button onClick={onClose} style={{ ...BTN_PRIMARY, padding: '0.25rem 0.8rem', fontSize: '0.78rem' }}>Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
});

// ============================================
// Import wizard
// ============================================

const ImportWizard = ({ isOpen, onClose, jobNumber, listId, columns, onImported, apiCall }) => {
  const fileRef = useRef(null);
  const [step, setStep] = useState(0); // 0 pick file, 1 configure, 2 running
  const [file, setFile] = useState(null);
  const [fileKind, setFileKind] = useState(null); // 'xer' | 'excel'
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);
  const [preview, setPreview] = useState({ headers: [], rows: [] });
  const [included, setIncluded] = useState({});
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState('append');
  const [matchExisting, setMatchExisting] = useState('');
  const [matchIncoming, setMatchIncoming] = useState('');
  const [appendUnmatched, setAppendUnmatched] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setStep(0); setFile(null); setFileKind(null); setWorkbook(null); setSheetNames([]);
    setSheetName(''); setHeaderRow(1); setPreview({ headers: [], rows: [] });
    setIncluded({}); setLabel(''); setMode('append'); setMatchExisting(''); setMatchIncoming('');
    setAppendUnmatched(true); setBusy(false); setError(null);
  };

  const close = () => { reset(); onClose(); };

  const readSheet = useCallback((wb, name, hdrRow) => {
    const ws = wb.getWorksheet(name);
    if (!ws) return { headers: [], rows: [] };
    const headers = [];
    const hdr = ws.getRow(hdrRow);
    hdr.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const t = String(cellText(cell.value)).trim();
      if (t) headers.push({ col: colNumber, label: t });
    });
    const rows = [];
    for (let r = hdrRow + 1; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      const obj = {};
      let hasValue = false;
      for (const h of headers) {
        const v = cellText(row.getCell(h.col).value);
        if (v !== '' && v !== null && v !== undefined) hasValue = true;
        obj[h.label] = v;
      }
      if (hasValue) rows.push(obj);
    }
    return { headers, rows };
  }, []);

  const handleFile = async (f) => {
    setError(null);
    setFile(f);
    setLabel(f.name.replace(/\.(xer|xlsx|xls|xlsm)$/i, ''));
    if (/\.xer$/i.test(f.name)) {
      setFileKind('xer');
      setStep(1);
      return;
    }
    try {
      const buffer = await f.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      const names = wb.worksheets.map((ws) => ws.name);
      setWorkbook(wb);
      setSheetNames(names);
      const first = names[0] || '';
      setSheetName(first);
      const p = readSheet(wb, first, 1);
      setPreview(p);
      setIncluded(Object.fromEntries(p.headers.map((h) => [h.label, true])));
      setFileKind('excel');
      setStep(1);
    } catch (e) {
      setError(`Could not read this Excel file: ${e.message}`);
    }
  };

  const changeSheet = (name, hdrRow) => {
    setSheetName(name);
    setHeaderRow(hdrRow);
    const p = readSheet(workbook, name, hdrRow);
    setPreview(p);
    setIncluded(Object.fromEntries(p.headers.map((h) => [h.label, true])));
    setMatchIncoming('');
  };

  const runImport = async () => {
    setBusy(true);
    setError(null);
    try {
      let result;
      if (fileKind === 'xer') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('label', label.trim());
        fd.append('mode', mode);
        if (listId) fd.append('listId', String(listId));
        if (mode === 'merge') {
          fd.append('matchKey', matchExisting);
          fd.append('appendUnmatched', String(appendUnmatched));
        }
        result = await apiCall('POST', `/master-components/${jobNumber}/import/xer`, fd);
      } else {
        const existingCol = columns.find((c) => c.column_key === matchExisting);
        const includedHeaders = preview.headers.filter((h) => included[h.label]);
        // For merge: rename the incoming match column to the existing column's label
        // so the server maps it onto the same registry key.
        const labelMap = {};
        for (const h of includedHeaders) {
          labelMap[h.label] = (mode === 'merge' && existingCol && h.label === matchIncoming)
            ? existingCol.label
            : h.label;
        }
        const rows = preview.rows.map((r) => {
          const out = {};
          for (const h of includedHeaders) out[labelMap[h.label]] = r[h.label];
          return out;
        });
        result = await apiCall('POST', `/master-components/${jobNumber}/import/rows`, {
          label: label.trim(),
          fileName: file.name,
          listId,
          mode,
          matchKey: mode === 'merge' ? matchExisting : undefined,
          appendUnmatched,
          columns: includedHeaders.map((h) => ({ label: labelMap[h.label] })),
          rows,
        });
      }
      const d = result.data;
      onImported(`Imported "${label.trim()}": ${d.rowsInserted} rows added, ${d.rowsUpdated} rows merged, ${d.columnsAdded} new columns.`);
      close();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setBusy(false);
    }
  };

  const canRun = label.trim() && (mode === 'append' || (matchExisting && (fileKind === 'xer' || matchIncoming)));

  return (
    <Modal isOpen={isOpen} onClose={close} title="📥 Import Data" width="760px">
      {step === 0 && (
        <div>
          <p style={{ marginTop: 0, color: '#475569', fontSize: '0.9rem' }}>
            Import a Primavera <strong>XER schedule</strong> or an <strong>Excel sheet</strong> (MTO, detail estimate, module list, …).
            New columns are created automatically; you can append new rows or merge into existing ones.
          </p>
          <div
            onClick={() => fileRef.current?.click()}
            style={{ border: '2px dashed #94a3b8', borderRadius: 12, padding: '2.5rem', textAlign: 'center', cursor: 'pointer', color: '#475569', backgroundColor: '#f8fafc' }}
          >
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📂</div>
            Click to choose a .xer / .xlsx / .xlsm file
          </div>
          <input ref={fileRef} type="file" accept=".xer,.xlsx,.xlsm,.xls" style={{ display: 'none' }}
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
          {error && <div style={{ color: '#b91c1c', marginTop: 12, fontSize: '0.85rem' }}>{error}</div>}
        </div>
      )}

      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#475569', marginBottom: 4 }}>Source label (shown in the Sources column)</label>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder='e.g. "XER 20260719" or "Struct Details"' style={{ ...INPUT, width: '100%', boxSizing: 'border-box' }} />
            </div>
            {fileKind === 'excel' && (
              <>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#475569', marginBottom: 4 }}>Sheet</label>
                  <select value={sheetName} onChange={(e) => changeSheet(e.target.value, headerRow)} style={{ ...INPUT }}>
                    {sheetNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#475569', marginBottom: 4 }}>Header row</label>
                  <input type="number" min={1} value={headerRow} onChange={(e) => { const r = Math.max(1, parseInt(e.target.value, 10) || 1); changeSheet(sheetName, r); }} style={{ ...INPUT, width: 70 }} />
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem' }}>
              <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} />
              <strong>Append</strong>&nbsp;— add all rows as new rows
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem' }}>
              <input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} disabled={!columns.length} />
              <strong>Merge</strong>&nbsp;— fill columns into existing rows on a match key
            </label>
          </div>

          {mode === 'merge' && (
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: '0.8rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#475569', marginBottom: 4 }}>Match on existing column</label>
                <select value={matchExisting} onChange={(e) => setMatchExisting(e.target.value)} style={{ ...INPUT }}>
                  <option value="">— choose —</option>
                  {columns.map((c) => <option key={c.column_key} value={c.column_key}>{c.label}</option>)}
                </select>
              </div>
              {fileKind === 'excel' && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#475569', marginBottom: 4 }}>Incoming column with the match value</label>
                  <select value={matchIncoming} onChange={(e) => setMatchIncoming(e.target.value)} style={{ ...INPUT }}>
                    <option value="">— choose —</option>
                    {preview.headers.filter((h) => included[h.label]).map((h) => <option key={h.label} value={h.label}>{h.label}</option>)}
                  </select>
                </div>
              )}
              {fileKind === 'xer' && (
                <div style={{ fontSize: '0.8rem', color: '#0f766e', alignSelf: 'center' }}>
                  XER activities merge by matching the chosen existing column's values.
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', alignSelf: 'center' }}>
                <input type="checkbox" checked={appendUnmatched} onChange={(e) => setAppendUnmatched(e.target.checked)} />
                Append unmatched rows as new rows
              </label>
            </div>
          )}

          {fileKind === 'excel' && (
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#475569', marginBottom: 6 }}>
                Columns to import ({preview.headers.filter((h) => included[h.label]).length} of {preview.headers.length}) — {preview.rows.length} data rows detected
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                {preview.headers.map((h) => (
                  <label key={h.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: 20, padding: '0.2rem 0.6rem', cursor: 'pointer', backgroundColor: included[h.label] ? '#f0fdfa' : '#f8fafc' }}>
                    <input type="checkbox" checked={!!included[h.label]} onChange={() => setIncluded((p) => ({ ...p, [h.label]: !p[h.label] }))} />
                    {h.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {fileKind === 'xer' && (
            <div style={{ fontSize: '0.85rem', color: '#475569', backgroundColor: '#f8fafc', borderRadius: 8, padding: '0.8rem' }}>
              Every activity becomes a row with Activity ID, Activity Name, WBS, dates, hours, float, resources and all P6 UDFs.
              Column names get the label suffix — e.g. <em>Activity ID ({label.trim() || 'label'})</em> — so multiple schedule snapshots can sit side by side.
            </div>
          )}

          {error && <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button style={BTN} onClick={reset}>← Back</button>
            <button style={{ ...BTN_PRIMARY, opacity: canRun && !busy ? 1 : 0.5 }} disabled={!canRun || busy} onClick={runImport}>
              {busy ? 'Importing…' : `Import ${fileKind === 'xer' ? 'XER' : `${preview.rows.length} rows`}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ============================================
// Column manager
// ============================================

const ColumnManager = ({ isOpen, onClose, columns, hiddenCols, onToggleHidden, onRename, onReorder, onDelete, onAdd, onSetHiddenMany, pinnedCols, onTogglePin }) => {
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('text');
  const [newOp, setNewOp] = useState('diff');
  const [newColA, setNewColA] = useState('');
  const [newColB, setNewColB] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [query, setQuery] = useState('');
  const [groupSel, setGroupSel] = useState(() => new Set()); // multi-select source filter
  const [visFilter, setVisFilter] = useState('all'); // all | shown | hidden

  // Every source that actually feeds a column (shared columns belong to several)
  const colSources = (c) => (c.sources && c.sources.length ? c.sources : [c.column_group || 'Manual']);

  const groups = useMemo(() => {
    const counts = new Map();
    for (const c of columns) {
      for (const g of colSources(c)) counts.set(g, (counts.get(g) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [columns]);

  const filtered = useMemo(() => columns.filter((c) => {
    if (groupSel.size && !colSources(c).some((g) => groupSel.has(g))) return false;
    if (visFilter === 'shown' && hiddenCols.has(c.column_key)) return false;
    if (visFilter === 'hidden' && !hiddenCols.has(c.column_key)) return false;
    if (query && !c.label.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  }), [columns, groupSel, visFilter, query, hiddenCols]);

  const filterActive = query || groupSel.size > 0 || visFilter !== 'all';
  const toggleGroup = (g) => setGroupSel((prev) => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📊 Manage Columns" width="700px">
      {/* Filter bar: search + shown/hidden + multi-select source chips */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.55rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="🔍 Filter columns by name…" style={{ ...INPUT, flex: '1 1 200px' }} />
        <div style={{ display: 'flex', gap: 2, backgroundColor: '#f1f5f9', borderRadius: 8, padding: 2 }}>
          {[['all', 'All'], ['shown', 'Shown'], ['hidden', 'Hidden']].map(([id, lbl]) => (
            <button key={id} onClick={() => setVisFilter(id)}
              style={{ ...BTN, border: 'none', padding: '0.25rem 0.6rem', fontSize: '0.76rem', borderRadius: 6, backgroundColor: visFilter === id ? 'white' : 'transparent', fontWeight: visFilter === id ? 700 : 500, color: visFilter === id ? ACCENT : '#64748b', boxShadow: visFilter === id ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>
              {lbl}
            </button>
          ))}
        </div>
        {filterActive && (
          <button style={{ ...BTN, padding: '0.25rem 0.6rem', fontSize: '0.76rem', color: '#b91c1c' }} onClick={() => { setQuery(''); setGroupSel(new Set()); setVisFilter('all'); }}>✕ Clear</button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: '0.7rem' }}>
        {groups.map(([g, n]) => {
          const on = groupSel.has(g);
          return (
            <button key={g} onClick={() => toggleGroup(g)} title={`${n} column${n > 1 ? 's' : ''} from ${g} — click to filter (multi-select)`}
              style={{
                ...BTN, padding: '0.14rem 0.55rem', fontSize: '0.7rem', borderRadius: 12,
                border: `1.5px solid ${sourceColor(g)}`,
                backgroundColor: on ? sourceColor(g) : 'white',
                color: on ? 'white' : sourceColor(g), fontWeight: 600,
              }}>
              {g} · {n}
            </button>
          );
        })}
      </div>

      {/* Bulk show/hide for whatever is currently filtered */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '0.76rem', color: '#64748b' }}>{filtered.length} of {columns.length} columns{filterActive ? ' (filtered)' : ''}</span>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button style={{ ...BTN, padding: '0.2rem 0.55rem', fontSize: '0.74rem' }} disabled={!filtered.length} onClick={() => onSetHiddenMany(filtered.map((c) => c.column_key), false)}>👁 Show these</button>
          <button style={{ ...BTN, padding: '0.2rem 0.55rem', fontSize: '0.74rem' }} disabled={!filtered.length} onClick={() => onSetHiddenMany(filtered.map((c) => c.column_key), true)}>🚫 Hide these</button>
        </div>
      </div>

      <div style={{ maxHeight: '44vh', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {filtered.map((c) => {
          const i = columns.indexOf(c);
          const srcs = colSources(c);
          const g = groupOf(c);
          const isPinned = pinnedCols.includes(c.column_key);
          return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.42rem 0.7rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', borderLeft: `3px solid ${groupColor(g)}`, opacity: hiddenCols.has(c.column_key) ? 0.55 : 1 }}>
              <input type="checkbox" checked={!hiddenCols.has(c.column_key)} onChange={() => onToggleHidden(c.column_key)} title="Show/hide" />
              <span
                onClick={() => onTogglePin(c.column_key)}
                title={isPinned ? 'Unpin from the left side' : `Pin to the left side (max ${MAX_PINS})`}
                style={{ cursor: 'pointer', opacity: isPinned ? 1 : 0.3, fontSize: '0.8rem' }}
              >📌</span>
              {editingId === c.id ? (
                <input
                  value={editValue} autoFocus
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={() => { if (editValue.trim() && editValue !== c.label) onRename(c, editValue.trim()); setEditingId(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingId(null); }}
                  style={{ ...INPUT, flex: 1, padding: '0.2rem 0.5rem' }}
                />
              ) : (
                <span style={{ flex: 1, cursor: 'text', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => { setEditingId(c.id); setEditValue(c.label); }} title={`${c.label} — click to rename`}>
                  {c.label}
                </span>
              )}
              <span style={{ color: '#94a3b8', fontSize: '0.7rem', width: 46 }}>{c.formula ? '🧮 calc' : c.data_type}</span>
              {c.formula && (
                <span style={{ fontSize: '0.68rem', color: '#7c3aed', backgroundColor: '#f5f3ff', borderRadius: 10, padding: '0.1rem 0.5rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formulaText(c.formula, columns)}>
                  {formulaText(c.formula, columns)}
                </span>
              )}
              {srcs.slice(0, 2).map((s) => (
                <span key={s} style={{ fontSize: '0.68rem', color: 'white', backgroundColor: sourceColor(s), borderRadius: 10, padding: '0.1rem 0.5rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Fed by: ${srcs.join(', ')}`}>{s}</span>
              ))}
              {srcs.length > 2 && (
                <span style={{ fontSize: '0.68rem', color: '#475569', backgroundColor: '#f1f5f9', borderRadius: 10, padding: '0.1rem 0.45rem', whiteSpace: 'nowrap' }} title={`Fed by: ${srcs.join(', ')}`}>+{srcs.length - 2}</span>
              )}
              <button style={{ ...BTN, padding: '0.15rem 0.4rem' }} disabled={filterActive || i <= 0} onClick={() => onReorder(i, i - 1)} title={filterActive ? 'Clear filters to reorder' : 'Move up'}>↑</button>
              <button style={{ ...BTN, padding: '0.15rem 0.4rem' }} disabled={filterActive || i === columns.length - 1} onClick={() => onReorder(i, i + 1)} title={filterActive ? 'Clear filters to reorder' : 'Move down'}>↓</button>
              <button style={{ ...BTN, padding: '0.15rem 0.4rem', color: '#b91c1c' }} onClick={() => onDelete(c)} title="Delete column">🗑</button>
            </div>
          );
        })}
        {!filtered.length && <div style={{ padding: '1.2rem', color: '#94a3b8', fontSize: '0.85rem' }}>{columns.length ? 'No columns match these filters.' : 'No columns yet — import a file or add a column below.'}</div>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem', flexWrap: 'wrap' }}>
        <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New column name…" style={{ ...INPUT, flex: '1 1 160px' }} />
        <select value={newType} onChange={(e) => setNewType(e.target.value)} style={INPUT}>
          <option value="text">Text</option><option value="number">Number</option><option value="date">Date</option>
          <option value="computed">🧮 Computed</option>
        </select>
        {newType === 'computed' && (
          <>
            <select value={newColA} onChange={(e) => setNewColA(e.target.value)} style={{ ...INPUT, maxWidth: 170 }} title="Column A">
              <option value="">A: — column —</option>
              {columns.filter((c) => !c.formula).map((c) => <option key={c.column_key} value={c.column_key}>{c.label}</option>)}
            </select>
            <select value={newOp} onChange={(e) => setNewOp(e.target.value)} style={INPUT} title="Operation">
              {FORMULA_OPS.map(([op, lbl]) => <option key={op} value={op}>{lbl}</option>)}
            </select>
            <select value={newColB} onChange={(e) => setNewColB(e.target.value)} style={{ ...INPUT, maxWidth: 170 }} title="Column B">
              <option value="">B: — column —</option>
              {columns.filter((c) => !c.formula).map((c) => <option key={c.column_key} value={c.column_key}>{c.label}</option>)}
            </select>
          </>
        )}
        <button
          style={BTN_PRIMARY}
          disabled={!newLabel.trim() || (newType === 'computed' && (!newColA || !newColB))}
          onClick={() => {
            if (newType === 'computed') onAdd(newLabel.trim(), 'number', { op: newOp, a: newColA, b: newColB });
            else onAdd(newLabel.trim(), newType);
            setNewLabel(''); setNewColA(''); setNewColB('');
          }}
        >➕ Add</button>
      </div>
      <p style={{ fontSize: '0.73rem', color: '#94a3b8', marginBottom: 0 }}>Colored chip = which import/file the column came from. 📌 pins a column to the left while scrolling. 🧮 Computed columns calculate live from two other columns — perfect for schedule slip (finish − finish) or hour deltas between snapshots. Click a name to rename. Reordering needs filters cleared. Deleting a column removes its data from every row.</p>
    </Modal>
  );
};

// ============================================
// Masters manager (multiple master lists per job)
// ============================================

const ManageListsModal = ({ isOpen, onClose, lists, activeListId, onCreate, onRename, onDelete, onSwitch }) => {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🗂 Manage Masters" width="520px">
      <p style={{ marginTop: 0, color: '#475569', fontSize: '0.85rem' }}>
        Each master is its own independent table — own columns, rows, imports and views.
        Use separate masters e.g. for different scopes, phases or what-if snapshots.
      </p>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: '0.8rem' }}>
        {lists.map((l) => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.7rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.87rem', backgroundColor: l.id === activeListId ? '#f0fdfa' : 'white' }}>
            <span style={{ width: 16 }}>{l.id === activeListId ? '✅' : ''}</span>
            {editingId === l.id ? (
              <input
                value={editValue} autoFocus
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => { if (editValue.trim() && editValue.trim() !== l.name) onRename(l, editValue.trim()); setEditingId(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingId(null); }}
                style={{ ...INPUT, flex: 1, padding: '0.2rem 0.5rem' }}
              />
            ) : (
              <span
                style={{ flex: 1, cursor: 'pointer', fontWeight: l.id === activeListId ? 700 : 500 }}
                onClick={() => onSwitch(l.id)}
                onDoubleClick={() => { setEditingId(l.id); setEditValue(l.name); }}
                title="Click to switch — double-click to rename"
              >
                {l.name}
              </span>
            )}
            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{Number(l.row_count || 0).toLocaleString()} rows</span>
            <button
              style={{ ...BTN, padding: '0.15rem 0.4rem', color: '#b91c1c' }}
              disabled={lists.length <= 1}
              onClick={() => onDelete(l)}
              title={lists.length <= 1 ? 'Every job needs at least one master' : 'Delete this master (rows are soft-deleted)'}
            >🗑</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder='New master name… e.g. "Phase 2" or "What-if"' style={{ ...INPUT, flex: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) { onCreate(newName.trim()); setNewName(''); } }} />
        <button style={BTN_PRIMARY} disabled={!newName.trim()} onClick={() => { onCreate(newName.trim()); setNewName(''); }}>➕ Create</button>
      </div>
    </Modal>
  );
};

// ============================================
// Saved views (named filter/sort/column layouts)
// ============================================

const ViewsModal = ({ isOpen, onClose, views, onApply, onSave, onDelete, hasState }) => {
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🔖 Saved Views" width="560px">
      <p style={{ marginTop: 0, color: '#475569', fontSize: '0.85rem' }}>
        A view remembers your search, filters, sort, hidden columns and pinned columns.
        Shared views (🌐) are visible to everyone on the project; personal ones (👤) only to you.
      </p>
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: '0.9rem', maxHeight: '38vh', overflowY: 'auto' }}>
        {views.length === 0 && <div style={{ padding: '1.2rem', color: '#94a3b8', fontSize: '0.85rem' }}>No saved views yet — set up the grid the way you like it, then save it below.</div>}
        {views.map((v) => (
          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.7rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.87rem' }}>
            <span title={v.is_shared ? 'Shared with the team' : 'Personal view'}>{v.is_shared ? '🌐' : '👤'}</span>
            <span style={{ flex: 1, cursor: 'pointer', fontWeight: 600, color: '#0f766e' }} onClick={() => onApply(v)} title="Click to apply">{v.name}</span>
            <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{v.created_by ? v.created_by.split('@')[0] : ''}</span>
            <button style={{ ...BTN, padding: '0.15rem 0.5rem', fontSize: '0.78rem' }} onClick={() => onApply(v)}>Apply</button>
            <button style={{ ...BTN, padding: '0.15rem 0.4rem', color: '#b91c1c' }} onClick={() => onDelete(v)} title="Delete (creator only)">🗑</button>
          </div>
        ))}
      </div>
      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.7rem' }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 6 }}>Save the current layout as a view</div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="View name…" style={{ ...INPUT, flex: '1 1 160px' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> 🌐 Share with team
          </label>
          <button
            style={{ ...BTN_PRIMARY, opacity: name.trim() && !busy ? 1 : 0.5 }}
            disabled={!name.trim() || busy}
            onClick={async () => { setBusy(true); try { await onSave(name.trim(), shared); setName(''); setShared(false); } finally { setBusy(false); } }}
          >💾 Save</button>
        </div>
        {!hasState && <div style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: 5 }}>Tip: the grid is currently in its default state — filter/sort/pin something first if you want the view to capture it.</div>}
      </div>
    </Modal>
  );
};

// ============================================
// Virtualized grid row (memoized — only edge rows re-render while scrolling)
// ============================================

const GridRow = memo(({ row, idx, visibleColumns, pinnedCount, isSelected, editingCell, onToggleSelect, onStartEdit, onEditChange, onEditSave, onEditCancel }) => {
  const bg = isSelected ? '#f0fdfa' : idx % 2 ? '#fafafa' : 'white';
  const renderCell = (c, i) => {
    const pinned = i < pinnedCount;
    const isEditing = editingCell && editingCell.colKey === c.column_key;
    const raw = c.formula ? computeFormula(c.formula, row.attrs) : row.attrs[c.column_key];
    const editable = c.is_editable && !c.formula;
    return (
      <td
        key={c.column_key}
        style={{
          ...TD_V,
          cursor: editable ? 'cell' : 'default',
          textAlign: c.data_type === 'number' ? 'right' : 'left',
          ...(c.formula ? { color: '#7c3aed' } : null),
          ...(pinned ? {
            position: 'sticky', left: CHECK_W + i * PIN_W, zIndex: 1, backgroundColor: bg,
            width: PIN_W, minWidth: PIN_W, maxWidth: PIN_W,
            borderRight: i === pinnedCount - 1 ? '2px solid #cbd5e1' : undefined,
          } : null),
        }}
        onClick={() => editable && !isEditing && onStartEdit(row.id, c.column_key, String(row.attrs[c.column_key] ?? ''))}
        title={raw !== undefined && raw !== null ? String(raw) : ''}
      >
        {isEditing ? (
          <input
            autoFocus value={editingCell.value}
            onChange={(e) => onEditChange(e.target.value)}
            onBlur={onEditSave}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') onEditCancel(); }}
            style={{ ...INPUT, padding: '0.15rem 0.4rem', width: '100%', boxSizing: 'border-box', fontSize: '0.85rem' }}
          />
        ) : raw === undefined || raw === null || raw === '' ? (
          <span style={{ color: '#e2e8f0' }}>·</span>
        ) : c.data_type === 'number' ? fmtNum(raw) : String(raw)}
      </td>
    );
  };
  return (
    <tr style={{ height: ROW_H, backgroundColor: bg }}>
      <td style={{ ...TD_V, textAlign: 'center', position: 'sticky', left: 0, zIndex: 1, backgroundColor: bg, width: CHECK_W, minWidth: CHECK_W }}>
        <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(row.id)} />
      </td>
      {visibleColumns.slice(0, pinnedCount).map((c, i) => renderCell(c, i))}
      <td style={{ ...TD_V, maxWidth: 220 }}>
        {(row.sources || []).map((s) => (
          <span key={s} style={{ fontSize: '0.68rem', color: 'white', backgroundColor: sourceColor(s), borderRadius: 10, padding: '0.1rem 0.45rem', marginRight: 4, display: 'inline-block' }} title={s}>
            {s.length > 22 ? `${s.slice(0, 21)}…` : s}
          </span>
        ))}
      </td>
      {visibleColumns.slice(pinnedCount).map((c, i) => renderCell(c, pinnedCount + i))}
    </tr>
  );
});

// ============================================
// Main component
// ============================================

const MasterComponentList = ({ jobNumber, projectId }) => {
  const { instance, accounts } = useMsal();
  const apiCall = useCallback(
    (method, url, data = null, config = {}) => authenticatedApiCall(instance, accounts, method, url, data, config),
    [instance, accounts]
  );

  const [view, setView] = useState('grid'); // grid | analytics | health | imports
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState(null);
  const [views, setViews] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [imports, setImports] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthKey, setHealthKey] = useState('');
  const [healthLoading, setHealthLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [filterValues, setFilterValues] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`mcl-hidden-${jobNumber}`) || '[]')); } catch { return new Set(); }
  });
  const [pinnedCols, setPinnedCols] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null); // { rowId, colKey, value }
  const [showImport, setShowImport] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showLists, setShowLists] = useState(false);
  const [showViews, setShowViews] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [addRowValues, setAddRowValues] = useState({});
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  }, []);

  // Live data push
  const fetchRef = useRef(null);
  const handleDataChange = useCallback(() => { if (fetchRef.current) fetchRef.current(); }, []);
  const { viewers, isConnected, connectionState } = usePresence('masterComponents', projectId || 'global', true, handleDataChange);

  // ---------------- data loading ----------------

  const listParam = useCallback((params) => {
    if (listId) params.set('listId', String(listId));
    return params;
  }, [listId]);

  const loadLists = useCallback(async () => {
    const r = await apiCall('GET', `/master-components/${jobNumber}/lists`);
    setLists(r.data.lists || []);
    return r.data.lists || [];
  }, [apiCall, jobNumber]);

  const loadViews = useCallback(async () => {
    const params = listParam(new URLSearchParams());
    const r = await apiCall('GET', `/master-components/${jobNumber}/views?${params.toString()}`);
    setViews(r.data.views || []);
  }, [apiCall, jobNumber, listParam]);

  const loadColumns = useCallback(async () => {
    const params = listParam(new URLSearchParams());
    const r = await apiCall('GET', `/master-components/${jobNumber}/columns?${params.toString()}`);
    setColumns(r.data.columns || []);
    return r.data.columns || [];
  }, [apiCall, jobNumber, listParam]);

  const loadRows = useCallback(async () => {
    // Load everything (server still applies search/filters/sort); the grid virtualizes rendering.
    const params = listParam(new URLSearchParams({ pageSize: 'all' }));
    if (search) params.set('search', search);
    if (sortConfig.key) { params.set('sortKey', sortConfig.key); params.set('sortDir', sortConfig.dir); }
    const activeFilters = Object.fromEntries(Object.entries(columnFilters).filter(([k, v]) => k !== '__sources' && v?.length));
    if (Object.keys(activeFilters).length) params.set('filters', JSON.stringify(activeFilters));
    if (columnFilters.__sources?.length) params.set('sources', JSON.stringify(columnFilters.__sources));
    const r = await apiCall('GET', `/master-components/${jobNumber}/rows?${params.toString()}`);
    setRows(r.data.rows || []);
    setTotal(r.data.total || 0);
  }, [apiCall, jobNumber, search, sortConfig, columnFilters, listParam]);

  const loadStats = useCallback(async () => {
    const params = listParam(new URLSearchParams());
    const r = await apiCall('GET', `/master-components/${jobNumber}/stats?${params.toString()}`);
    setStats(r.data);
  }, [apiCall, jobNumber, listParam]);

  const loadImports = useCallback(async () => {
    const params = listParam(new URLSearchParams());
    const r = await apiCall('GET', `/master-components/${jobNumber}/imports?${params.toString()}`);
    setImports(r.data.imports || []);
  }, [apiCall, jobNumber, listParam]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const params = listParam(new URLSearchParams());
      if (healthKey) params.set('keyColumn', healthKey);
      const r = await apiCall('GET', `/master-components/${jobNumber}/health?${params.toString()}`);
      setHealth(r.data);
      if (!healthKey && r.data.keyColumn) setHealthKey(r.data.keyColumn);
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to compute health', 'error');
    } finally {
      setHealthLoading(false);
    }
  }, [apiCall, jobNumber, listParam, healthKey, showToast]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadColumns(), loadRows(), loadStats(), loadImports(), loadViews()]);
    } catch (e) {
      showToast(e.response?.data?.error || `Failed to load: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [loadColumns, loadRows, loadStats, loadImports, loadViews, showToast]);

  useEffect(() => { fetchRef.current = loadAll; }, [loadAll]);

  // Boot: resolve the master list first, then everything loads scoped to it
  useEffect(() => {
    if (!jobNumber) return;
    (async () => {
      try {
        const ls = await loadLists();
        const saved = parseInt(localStorage.getItem(`mcl-list-${jobNumber}`), 10);
        setListId(ls.find((l) => l.id === saved) ? saved : (ls[0]?.id ?? null));
      } catch (e) {
        showToast(e.response?.data?.error || `Failed to load masters: ${e.message}`, 'error');
        setLoading(false);
      }
    })();
  }, [jobNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (jobNumber && listId) loadAll(); }, [listId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (jobNumber && listId && !loading) loadRows().catch(() => {}); }, [search, sortConfig, columnFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (jobNumber && listId && view === 'health') loadHealth(); }, [view, listId, healthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Hidden + pinned columns persist per job+list
  useEffect(() => {
    if (!listId) return;
    try {
      setHiddenCols(new Set(JSON.parse(
        localStorage.getItem(`mcl-hidden-${jobNumber}-${listId}`)
        || localStorage.getItem(`mcl-hidden-${jobNumber}`) // legacy key (pre-lists)
        || '[]'
      )));
    } catch { setHiddenCols(new Set()); }
    try { setPinnedCols(JSON.parse(localStorage.getItem(`mcl-pins-${jobNumber}-${listId}`) || '[]')); } catch { setPinnedCols([]); }
  }, [jobNumber, listId]);

  useEffect(() => {
    if (!listId) return;
    localStorage.setItem(`mcl-hidden-${jobNumber}-${listId}`, JSON.stringify([...hiddenCols]));
  }, [hiddenCols, jobNumber, listId]);
  useEffect(() => {
    if (!listId) return;
    localStorage.setItem(`mcl-pins-${jobNumber}-${listId}`, JSON.stringify(pinnedCols));
  }, [pinnedCols, jobNumber, listId]);

  // Pinned columns come first so their sticky offsets are contiguous
  const visibleColumns = useMemo(() => {
    const vis = columns.filter((c) => !hiddenCols.has(c.column_key));
    if (!pinnedCols.length) return vis;
    const pinSet = new Set(pinnedCols);
    const pinned = pinnedCols.map((k) => vis.find((c) => c.column_key === k)).filter(Boolean);
    return [...pinned, ...vis.filter((c) => !pinSet.has(c.column_key))];
  }, [columns, hiddenCols, pinnedCols]);
  const pinnedCount = useMemo(
    () => pinnedCols.filter((k) => columns.some((c) => c.column_key === k) && !hiddenCols.has(k)).length,
    [pinnedCols, columns, hiddenCols]
  );
  const activeFilterCount = Object.values(columnFilters).filter((v) => v?.length).length;
  const computedCols = useMemo(() => columns.filter((c) => c.formula), [columns]);

  // Computed columns are sorted client-side (their values aren't stored server-side)
  const displayRows = useMemo(() => {
    const col = sortConfig.key ? columns.find((c) => c.column_key === sortConfig.key) : null;
    if (!col || !col.formula) return rows;
    const dir = sortConfig.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = computeFormula(col.formula, a.attrs);
      const vb = computeFormula(col.formula, b.attrs);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return (va - vb) * dir;
    });
  }, [rows, sortConfig, columns]);

  // ---------------- row virtualization ----------------

  const gridRef = useRef(null);
  const scrollRaf = useRef(null);
  const [winStart, setWinStart] = useState(0);
  const [winCount, setWinCount] = useState(80);

  const updateWindow = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const start = Math.max(0, Math.floor(el.scrollTop / ROW_H) - OVERSCAN);
    const count = Math.ceil((el.clientHeight || 600) / ROW_H) + OVERSCAN * 2;
    setWinStart((p) => (p === start ? p : start));
    setWinCount((p) => (p === count ? p : count));
  }, []);

  const onGridScroll = useCallback(() => {
    if (scrollRaf.current) return;
    scrollRaf.current = requestAnimationFrame(() => { scrollRaf.current = null; updateWindow(); });
  }, [updateWindow]);

  useEffect(() => { updateWindow(); }, [rows.length, updateWindow]);
  // Back to the top whenever the result set changes shape
  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = 0;
    setWinStart(0);
  }, [search, sortConfig, columnFilters]);


  // ---------------- handlers ----------------

  const resetGridState = () => {
    setColumnFilters({}); setSearch(''); setSearchInput('');
    setSortConfig({ key: null, dir: 'asc' });
    setSelectedRows(new Set()); setEditingCell(null);
    setHealth(null); setHealthKey('');
  };

  const switchList = (id) => {
    if (id === listId) return;
    localStorage.setItem(`mcl-list-${jobNumber}`, String(id));
    resetGridState();
    setListId(id);
    setShowLists(false);
  };

  const listCreate = async (name) => {
    try {
      const r = await apiCall('POST', `/master-components/${jobNumber}/lists`, { name });
      await loadLists();
      switchList(r.data.list.id);
      showToast(`Master "${name}" created — you're now in it`);
    } catch (e) { showToast(e.response?.data?.error || 'Failed to create master', 'error'); }
  };
  const listRename = async (list, name) => {
    try {
      await apiCall('PATCH', `/master-components/${jobNumber}/lists/${list.id}`, { name });
      loadLists();
    } catch (e) { showToast(e.response?.data?.error || 'Rename failed', 'error'); }
  };
  const listDelete = async (list) => {
    if (!window.confirm(`Delete master "${list.name}" with its ${Number(list.row_count || 0).toLocaleString()} rows and all its columns/views? Rows can be restored by an admin.`)) return;
    try {
      await apiCall('DELETE', `/master-components/${jobNumber}/lists/${list.id}`);
      const ls = await loadLists();
      if (list.id === listId && ls.length) switchList(ls[0].id);
      showToast(`Master "${list.name}" deleted`);
    } catch (e) { showToast(e.response?.data?.error || 'Delete failed', 'error'); }
  };

  const applyView = (v) => {
    const cfg = v.config || {};
    setSearchInput(cfg.search || ''); setSearch(cfg.search || '');
    setColumnFilters(cfg.filters || {});
    setSortConfig({ key: cfg.sortKey || null, dir: cfg.sortDir || 'asc' });
    setHiddenCols(new Set(cfg.hiddenCols || []));
    setPinnedCols(Array.isArray(cfg.pinnedCols) ? cfg.pinnedCols : []);
    setShowViews(false);
    showToast(`View "${v.name}" applied`);
  };
  const saveView = async (name, isShared) => {
    const config = {
      search, filters: columnFilters,
      sortKey: sortConfig.key, sortDir: sortConfig.dir,
      hiddenCols: [...hiddenCols], pinnedCols,
    };
    try {
      await apiCall('POST', `/master-components/${jobNumber}/views`, { name, isShared, config, listId });
      await loadViews();
      showToast(`View "${name}" saved${isShared ? ' and shared' : ''}`);
    } catch (e) { showToast(e.response?.data?.error || 'Failed to save view', 'error'); }
  };
  const deleteView = async (v) => {
    if (!window.confirm(`Delete view "${v.name}"?`)) return;
    try {
      await apiCall('DELETE', `/master-components/${jobNumber}/views/${v.id}`);
      loadViews();
    } catch (e) { showToast(e.response?.data?.error || 'Only the view\'s creator can delete it', 'error'); }
  };

  const togglePin = (key) => setPinnedCols((prev) => {
    if (prev.includes(key)) return prev.filter((k) => k !== key);
    if (prev.length >= MAX_PINS) {
      showToast(`Up to ${MAX_PINS} pinned columns — unpin one first`, 'info');
      return prev;
    }
    return [...prev, key];
  });

  // Light tracker hand-off: copy the row's key value, open the tracker in a new tab
  const openInTracker = () => {
    const rowId = [...selectedRows][0];
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    const keyCol = (health?.keyColumn && columns.find((c) => c.column_key === health.keyColumn)) || visibleColumns.find((c) => !c.formula);
    const val = keyCol ? String(row.attrs[keyCol.column_key] ?? '').trim() : '';
    if (val) { try { navigator.clipboard.writeText(val); } catch { /* clipboard unavailable */ } }
    window.open(`/project/${projectId}#matrix`, '_blank');
    showToast(val ? `"${val}" copied — paste it into the tracker's search` : 'Tracker opened in a new tab', 'info');
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  const openColumnFilter = async (e, colKey) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterPos({ top: rect.bottom + 4, left: rect.left });
    setOpenFilter(colKey);
    setFilterLoading(true);
    try {
      if (colKey === '__sources') {
        setFilterValues((stats?.bySource || []).map((s) => ({ value: s.source, count: s.rows })));
      } else {
        const params = listParam(new URLSearchParams({ column: colKey }));
        const r = await apiCall('GET', `/master-components/${jobNumber}/rows/distinct?${params.toString()}`);
        setFilterValues(r.data.values || []);
      }
    } catch { setFilterValues([]); }
    setFilterLoading(false);
  };

  const toggleFilterValue = (colKey, value) => {
    setColumnFilters((prev) => {
      const cur = prev[colKey] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      const out = { ...prev, [colKey]: next };
      if (!next.length) delete out[colKey];
      return out;
    });
  };

  const saveCell = async () => {
    if (!editingCell) return;
    const { rowId, colKey, value } = editingCell;
    setEditingCell(null);
    const row = rows.find((r) => r.id === rowId);
    if (!row || String(row.attrs[colKey] ?? '') === value) return;
    try {
      const r = await apiCall('PATCH', `/master-components/${jobNumber}/rows/${rowId}`, { attrs: { [colKey]: value } });
      setRows((prev) => prev.map((x) => (x.id === rowId ? r.data.row : x)));
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to save cell', 'error');
    }
  };

  // Stable row callbacks so memoized GridRows skip re-rendering while scrolling
  const saveCellRef = useRef(null);
  saveCellRef.current = saveCell;
  const rowToggleSelect = useCallback((id) => setSelectedRows((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  const rowStartEdit = useCallback((rowId, colKey, value) => setEditingCell({ rowId, colKey, value }), []);
  const rowEditChange = useCallback((value) => setEditingCell((p) => (p ? { ...p, value } : p)), []);
  const rowEditSave = useCallback(() => { if (saveCellRef.current) saveCellRef.current(); }, []);
  const rowEditCancel = useCallback(() => setEditingCell(null), []);

  const handleBulkDelete = async () => {
    if (!selectedRows.size) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected row(s)? They can be restored by an admin.`)) return;
    try {
      await apiCall('POST', `/master-components/${jobNumber}/rows/bulk-delete`, { ids: [...selectedRows] });
      setSelectedRows(new Set());
      showToast('Rows deleted');
      loadRows(); loadStats();
    } catch (e) {
      showToast(e.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleAddRow = async () => {
    const attrs = Object.fromEntries(Object.entries(addRowValues).filter(([, v]) => v !== ''));
    if (!Object.keys(attrs).length) return;
    try {
      await apiCall('POST', `/master-components/${jobNumber}/rows`, { attrs, listId });
      setShowAddRow(false);
      setAddRowValues({});
      showToast('Row added');
      loadRows(); loadStats();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to add row', 'error');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = listParam(new URLSearchParams());
      const r = await apiCall('GET', `/master-components/${jobNumber}/export?${params.toString()}`, null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `master-components-${jobNumber}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Column manager actions
  const cmToggleHidden = (key) => setHiddenCols((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const cmSetHiddenMany = (keys, hidden) => setHiddenCols((prev) => {
    const next = new Set(prev);
    for (const k of keys) { if (hidden) next.add(k); else next.delete(k); }
    return next;
  });
  const cmRename = async (col, label) => {
    try {
      await apiCall('PATCH', `/master-components/${jobNumber}/columns/${col.id}`, { label });
      loadColumns();
    } catch (e) { showToast(e.response?.data?.error || 'Rename failed', 'error'); }
  };
  const cmReorder = async (from, to) => {
    const next = [...columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setColumns(next);
    try {
      await apiCall('POST', `/master-components/${jobNumber}/columns/reorder`, { order: next.map((c) => c.column_key), listId });
    } catch { loadColumns(); }
  };
  const cmDelete = async (col) => {
    if (!window.confirm(`Delete column "${col.label}" and its data from every row?`)) return;
    try {
      await apiCall('DELETE', `/master-components/${jobNumber}/columns/${col.id}`);
      loadColumns(); loadRows();
    } catch (e) { showToast(e.response?.data?.error || 'Delete failed', 'error'); }
  };
  const cmAdd = async (label, dataType, formula = null) => {
    try {
      await apiCall('POST', `/master-components/${jobNumber}/columns`, { label, dataType, formula, listId });
      loadColumns();
      if (formula) showToast(`Computed column "${label}" added — it calculates live on every row`);
    } catch (e) { showToast(e.response?.data?.error || 'Add failed', 'error'); }
  };

  // ---------------- render ----------------

  const viewTab = (id, label, icon) => (
    <button
      key={id}
      onClick={() => setView(id)}
      style={{
        ...BTN, border: 'none', borderRadius: 6, padding: '0.32rem 0.7rem', fontSize: '0.82rem',
        backgroundColor: view === id ? 'white' : 'transparent', fontWeight: view === id ? 700 : 500,
        color: view === id ? ACCENT : '#64748b', boxShadow: view === id ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      {icon} {label}
    </button>
  );

  return (
    <div style={{ padding: '0.75rem', backgroundColor: '#f1f5f9', minHeight: '100%' }}>
      {/* Compact single-line header: title, stats, tabs, search and every action */}
      <div style={{ backgroundColor: 'white', borderRadius: 12, padding: '0.45rem 0.8rem', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <h1 style={{ margin: 0, fontSize: '1rem', color: '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          🧩 Master Component List
        </h1>
        {/* Master switcher: each master is an independent table */}
        {lists.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <select
              value={listId || ''}
              onChange={(e) => switchList(parseInt(e.target.value, 10))}
              title="Switch between masters (independent tables)"
              style={{ ...INPUT, padding: '0.28rem 0.4rem', fontSize: '0.8rem', fontWeight: 600, color: ACCENT, maxWidth: 170 }}
            >
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <button style={{ ...BTN, padding: '0.28rem 0.4rem', fontSize: '0.8rem' }} onClick={() => setShowLists(true)} title="Manage masters — create, rename, delete">⚙</button>
          </span>
        )}
        <span
          style={{ fontSize: '0.74rem', color: '#64748b', whiteSpace: 'nowrap' }}
          title={`${stats?.importCount ?? 0} imports • last: ${stats?.lastImportAt ? new Date(stats.lastImportAt).toLocaleString() : '—'}`}
        >
          {(stats?.rowCount ?? 0).toLocaleString()} rows · {stats?.columnCount ?? 0} cols · {stats?.bySource?.length ?? 0} sources
        </span>
        <div style={{ display: 'flex', gap: 2, backgroundColor: '#f1f5f9', borderRadius: 8, padding: 2 }}>
          {viewTab('grid', 'Grid', '🗒️')}
          {viewTab('analytics', 'Analytics', '📈')}
          {viewTab('health', 'Health', '🩺')}
          {viewTab('imports', 'Data Flow', '🔀')}
        </div>
        <input type="text" placeholder="🔍 Search all columns…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} style={{ ...INPUT, padding: '0.32rem 0.6rem', width: 190, fontSize: '0.82rem' }} />
        {activeFilterCount > 0 && (
          <button style={{ ...BTN, padding: '0.3rem 0.6rem', color: '#b91c1c', fontSize: '0.76rem' }} onClick={() => setColumnFilters({})}>
            ✕ Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {selectedRows.size === 1 && (
          <button style={{ ...BTN, padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={openInTracker} title="Copy this row's key value and open the progress tracker in a new tab">🔎 Tracker</button>
        )}
        {selectedRows.size > 0 && (
          <button style={{ ...BTN, padding: '0.3rem 0.6rem', color: '#b91c1c', fontSize: '0.8rem' }} onClick={handleBulkDelete}>🗑 Delete {selectedRows.size}</button>
        )}
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={3} showConnectionStatus={false} />
        <button style={{ ...BTN, padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => setShowViews(true)} title="Saved views — named filter/sort/column layouts">🔖 Views{views.length ? ` (${views.length})` : ''}</button>
        <button style={{ ...BTN, padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => setShowColumns(true)}>📊 Columns</button>
        <button style={{ ...BTN, padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => { setAddRowValues({}); setShowAddRow(true); }}>➕ Add Row</button>
        <button style={{ ...BTN, padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => setShowHelp(true)}>❓ Help</button>
        <button style={{ ...BTN, padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={handleExport} disabled={exporting}>{exporting ? '⏳' : '📤'} Export</button>
        <button style={{ ...BTN_PRIMARY, padding: '0.3rem 0.7rem', fontSize: '0.8rem' }} onClick={() => setShowImport(true)}>📥 Import</button>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        {view === 'grid' && (
          <>
            {/* Active filter chips */}
            {activeFilterCount > 0 && (
              <div style={{ padding: '0.5rem 1rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', borderBottom: '1px solid #f1f5f9' }}>
                {Object.entries(columnFilters).filter(([, v]) => v?.length).map(([key, values]) => {
                  const col = key === '__sources' ? { label: 'Sources' } : columns.find((c) => c.column_key === key);
                  return (
                    <span key={key} style={{ fontSize: '0.75rem', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', color: '#0f766e', borderRadius: 14, padding: '0.15rem 0.6rem', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <strong>{col?.label || key}:</strong> {values.slice(0, 3).join(', ')}{values.length > 3 ? ` +${values.length - 3}` : ''}
                      <span style={{ cursor: 'pointer' }} onClick={() => setColumnFilters((p) => { const n = { ...p }; delete n[key]; return n; })}>✕</span>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Grid */}
            {/* Height budgeted so the whole page fits the viewport without scrolling */}
            <div ref={gridRef} onScroll={onGridScroll} style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 345px)', minHeight: 240, overflowY: 'auto' }}>
              {/* borderCollapse must be 'separate': collapsed borders belong to the table, not the cells,
                  so they don't stick with the sticky header and rows show through the border lines */}
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
                <thead>
                  {/* Source header: which import/file feeds each column — repeated above every column */}
                  <tr>
                    <th style={{ ...TH, top: 0, height: 30, boxSizing: 'border-box', padding: '0.15rem 0.6rem', borderBottom: '1px solid #e2e8f0', zIndex: 5, position: 'sticky', left: 0, width: CHECK_W, minWidth: CHECK_W }} />
                    {(() => {
                      const groupTh = (c, i, pinned) => {
                        const g = c.formula ? '🧮 Computed' : groupOf(c);
                        const clr = c.formula ? '#7c3aed' : groupColor(g);
                        const prev = i > 0 ? visibleColumns[i - 1] : null;
                        const prevG = prev ? (prev.formula ? '🧮 Computed' : groupOf(prev)) : null;
                        const runStart = i === 0 || prevG !== g;
                        return (
                          <th
                            key={`grp-${c.column_key}`}
                            style={{
                              ...TH, top: 0, height: 30, boxSizing: 'border-box', padding: '0.15rem 0.6rem',
                              borderBottom: '1px solid #e2e8f0',
                              borderLeft: runStart ? `3px solid ${clr}` : `1px solid ${clr}33`,
                              color: clr,
                              // Opaque background (solid base + tint layer) — an alpha-only bg lets rows show through the sticky header
                              backgroundColor: '#f8fafc',
                              backgroundImage: `linear-gradient(${clr}0d, ${clr}0d)`,
                              fontSize: '0.66rem', textTransform: 'none', letterSpacing: 0, fontWeight: 600,
                              maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis',
                              ...(pinned
                                ? { zIndex: 5, position: 'sticky', left: CHECK_W + i * PIN_W, width: PIN_W, minWidth: PIN_W }
                                : { zIndex: 3 }),
                            }}
                            title={c.formula ? `Computed: ${formulaText(c.formula, columns)}` : (g === SHARED_GROUP ? `Fed by: ${(c.sources || []).join(', ')}` : `Source: ${g}`)}
                          >
                            {c.formula ? '🧮 Computed' : (g === SHARED_GROUP ? `🔗 Shared (${(c.sources || []).length})` : g)}
                          </th>
                        );
                      };
                      return (
                        <>
                          {visibleColumns.slice(0, pinnedCount).map((c, i) => groupTh(c, i, true))}
                          <th style={{ ...TH, top: 0, height: 30, boxSizing: 'border-box', padding: '0.15rem 0.6rem', borderBottom: '1px solid #e2e8f0', zIndex: 3 }} />
                          {visibleColumns.slice(pinnedCount).map((c, i) => groupTh(c, pinnedCount + i, false))}
                        </>
                      );
                    })()}
                  </tr>
                  <tr>
                    <th style={{ ...TH, top: 30, width: CHECK_W, minWidth: CHECK_W, boxSizing: 'border-box', textAlign: 'center', position: 'sticky', left: 0, zIndex: 5 }}>
                      <input type="checkbox"
                        checked={rows.length > 0 && selectedRows.size >= rows.length}
                        onChange={(e) => setSelectedRows(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
                    </th>
                    {(() => {
                      const labelTh = (c, i, pinned) => {
                        const isPinned = pinnedCols.includes(c.column_key);
                        return (
                          <th key={c.column_key} style={{
                            ...TH, top: 30,
                            ...(pinned
                              ? { position: 'sticky', left: CHECK_W + i * PIN_W, width: PIN_W, minWidth: PIN_W, maxWidth: PIN_W, zIndex: 5, boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', borderRight: i === pinnedCount - 1 ? '2px solid #cbd5e1' : undefined }
                              : null),
                          }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span onClick={() => handleSort(c.column_key)} style={{ cursor: 'pointer' }} title={c.formula ? `Computed: ${formulaText(c.formula, columns)} — click to sort` : `${c.label} — source: ${(c.sources || []).join(', ') || c.column_group || 'Manual'}`}>
                                {c.formula ? '🧮 ' : ''}{displayLabel(c)}
                                {sortConfig.key === c.column_key && <span style={{ marginLeft: 3 }}>{sortConfig.dir === 'asc' ? '▲' : '▼'}</span>}
                              </span>
                              <span onClick={(e) => openColumnFilter(e, c.column_key)} style={{ cursor: 'pointer' }}>
                                <FilterIcon active={!!columnFilters[c.column_key]?.length} />
                              </span>
                              <span
                                onClick={() => togglePin(c.column_key)}
                                title={isPinned ? 'Unpin' : `Pin to the left (max ${MAX_PINS})`}
                                style={{ cursor: 'pointer', opacity: isPinned ? 1 : 0.25, fontSize: '0.72rem' }}
                              >📌</span>
                            </span>
                          </th>
                        );
                      };
                      return (
                        <>
                          {visibleColumns.slice(0, pinnedCount).map((c, i) => labelTh(c, i, true))}
                          <th style={{ ...TH, top: 30 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              Sources
                              <span onClick={(e) => openColumnFilter(e, '__sources')} style={{ cursor: 'pointer' }}>
                                <FilterIcon active={!!columnFilters.__sources?.length} />
                              </span>
                            </span>
                          </th>
                          {visibleColumns.slice(pinnedCount).map((c, i) => labelTh(c, pinnedCount + i, false))}
                        </>
                      );
                    })()}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={visibleColumns.length + 2} style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length + 2} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                        <div style={{ fontSize: '2rem', marginBottom: 8 }}>📭</div>
                        {columns.length ? 'No rows match the current filters.' : (
                          <span>Nothing here yet — click <strong>📥 Import</strong> to load an XER schedule or Excel sheet.</span>
                        )}
                      </td>
                    </tr>
                  ) : (() => {
                    // Virtual window: only render rows in/near the viewport; spacer rows keep the scrollbar honest
                    const end = Math.min(displayRows.length, winStart + winCount);
                    const topPad = winStart * ROW_H;
                    const botPad = Math.max(0, (displayRows.length - end) * ROW_H);
                    return (
                      <>
                        {topPad > 0 && <tr style={{ height: topPad }}><td colSpan={visibleColumns.length + 2} style={{ padding: 0, border: 'none' }} /></tr>}
                        {displayRows.slice(winStart, end).map((row, i) => (
                          <GridRow
                            key={row.id}
                            row={row}
                            idx={winStart + i}
                            visibleColumns={visibleColumns}
                            pinnedCount={pinnedCount}
                            isSelected={selectedRows.has(row.id)}
                            editingCell={editingCell && editingCell.rowId === row.id ? editingCell : null}
                            onToggleSelect={rowToggleSelect}
                            onStartEdit={rowStartEdit}
                            onEditChange={rowEditChange}
                            onEditSave={rowEditSave}
                            onEditCancel={rowEditCancel}
                          />
                        ))}
                        {botPad > 0 && <tr style={{ height: botPad }}><td colSpan={visibleColumns.length + 2} style={{ padding: 0, border: 'none' }} /></tr>}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div style={{ padding: '0.55rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: '0 0 12px 12px', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ color: '#64748b', fontSize: '0.82rem' }}>
                {total.toLocaleString()} rows{activeFilterCount || search ? ' (filtered)' : ''}
                {selectedRows.size > 0 && ` • ${selectedRows.size} selected`} • click a cell to edit
              </span>
              <span style={{ color: '#94a3b8', fontSize: '0.76rem' }}>all rows loaded — scroll freely</span>
            </div>
          </>
        )}

        {view === 'analytics' && (
          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <KPICard title="Components / Rows" value={(stats?.rowCount ?? 0).toLocaleString()} icon="🧾" color={ACCENT} subtitle="live rows" />
              <KPICard title="Columns" value={stats?.columnCount ?? 0} icon="📊" color="#b45309" subtitle="fluid data columns" />
              <KPICard title="Data Sources" value={stats?.bySource?.length ?? 0} icon="🗂️" color="#1d4ed8" subtitle={`${stats?.importCount ?? 0} imports`} />
              <KPICard title="Last Import" value={stats?.lastImportAt ? new Date(stats.lastImportAt).toLocaleDateString() : '—'} icon="⏱️" color="#7c3aed" subtitle={stats?.lastImportAt ? new Date(stats.lastImportAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'no imports yet'} />
            </div>
            <div>
              <h3 style={{ margin: '0 0 0.7rem', fontSize: '1rem', color: '#1e293b' }}>Rows by data source</h3>
              {(stats?.bySource || []).length === 0 && <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No data yet.</div>}
              {(stats?.bySource || []).map((s) => {
                const max = Math.max(...(stats?.bySource || []).map((x) => x.rows), 1);
                return (
                  <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ width: 220, fontSize: '0.82rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.source}>{s.source}</span>
                    <div style={{ flex: 1, height: 16, backgroundColor: '#f1f5f9', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${(s.rows / max) * 100}%`, height: '100%', backgroundColor: sourceColor(s.source), borderRadius: 8 }} />
                    </div>
                    <span style={{ width: 70, textAlign: 'right', fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>{s.rows.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
            <div>
              <h3 style={{ margin: '0 0 0.7rem', fontSize: '1rem', color: '#1e293b' }}>Columns by group</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {Object.entries(columns.reduce((acc, c) => { const g = c.column_group || 'Manual'; acc[g] = (acc[g] || 0) + 1; return acc; }, {})).map(([g, n]) => (
                  <span key={g} style={{ fontSize: '0.78rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: '0.25rem 0.7rem', color: '#334155' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, backgroundColor: sourceColor(g), marginRight: 6 }} />
                    {g}: <strong>{n}</strong>
                  </span>
                ))}
              </div>
            </div>

            {/* Biggest movers: top rows by absolute value of each computed column */}
            {computedCols.map((cc) => {
              const labelCol = columns.find((c) => !c.formula && c.column_key !== cc.formula.a && c.column_key !== cc.formula.b);
              const scored = rows
                .map((r) => ({ r, v: computeFormula(cc.formula, r.attrs) }))
                .filter((x) => x.v !== null && x.v !== 0)
                .sort((a, b) => Math.abs(b.v) - Math.abs(a.v))
                .slice(0, 12);
              if (!scored.length) return null;
              const max = Math.max(...scored.map((x) => Math.abs(x.v)), 1);
              return (
                <div key={cc.column_key}>
                  <h3 style={{ margin: '0 0 0.2rem', fontSize: '1rem', color: '#1e293b' }}>🧮 Biggest movers — {cc.label}</h3>
                  <p style={{ margin: '0 0 0.7rem', fontSize: '0.76rem', color: '#94a3b8' }}>{formulaText(cc.formula, columns)} · top {scored.length} rows by magnitude</p>
                  {scored.map(({ r, v }) => {
                    const rowLabel = labelCol ? String(r.attrs[labelCol.column_key] ?? '').trim() || '(blank)' : r.id.slice(0, 8);
                    return (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                        <span style={{ width: 220, fontSize: '0.8rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={rowLabel}>{rowLabel}</span>
                        <div style={{ flex: 1, height: 14, backgroundColor: '#f1f5f9', borderRadius: 7, overflow: 'hidden' }}>
                          <div style={{ width: `${(Math.abs(v) / max) * 100}%`, height: '100%', backgroundColor: v >= 0 ? '#16a34a' : '#dc2626', borderRadius: 7 }} />
                        </div>
                        <span style={{ width: 90, textAlign: 'right', fontSize: '0.8rem', fontWeight: 600, color: v >= 0 ? '#166534' : '#b91c1c' }}>{v >= 0 ? '+' : ''}{fmtNum(v)}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {view === 'health' && (
          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>🩺 Data health</h3>
              <label style={{ fontSize: '0.82rem', color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Key column:
                <select value={healthKey} onChange={(e) => setHealthKey(e.target.value)} style={{ ...INPUT, padding: '0.28rem 0.5rem', fontSize: '0.82rem', maxWidth: 260 }}>
                  <option value="">— auto (last merge key) —</option>
                  {columns.filter((c) => !c.formula).map((c) => <option key={c.column_key} value={c.column_key}>{c.label}</option>)}
                </select>
              </label>
              <button style={{ ...BTN, padding: '0.28rem 0.6rem', fontSize: '0.8rem' }} onClick={loadHealth} disabled={healthLoading}>{healthLoading ? '⏳' : '🔄'} Re-check</button>
            </div>

            {healthLoading && !health ? (
              <div style={{ color: '#94a3b8', padding: '2rem', textAlign: 'center' }}>Checking…</div>
            ) : !health ? (
              <div style={{ color: '#94a3b8', padding: '2rem', textAlign: 'center' }}>No health data yet.</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                  <KPICard title="Rows Checked" value={(health.totalRows ?? 0).toLocaleString()} icon="🧾" color={ACCENT} subtitle="live rows in this master" />
                  <KPICard
                    title="Duplicate Key Values" value={(health.duplicates || []).length}
                    icon="👯" color={(health.duplicates || []).length ? '#dc2626' : '#16a34a'}
                    subtitle={health.keyColumn ? `${health.duplicateRows || 0} rows share a ${columns.find((c) => c.column_key === health.keyColumn)?.label || health.keyColumn}` : 'pick a key column'}
                  />
                  <KPICard
                    title="Rows Missing Key" value={health.missingKey === null ? '—' : (health.missingKey ?? 0).toLocaleString()}
                    icon="🕳️" color={health.missingKey ? '#b45309' : '#16a34a'}
                    subtitle={health.keyColumn ? `blank ${columns.find((c) => c.column_key === health.keyColumn)?.label || health.keyColumn}` : 'pick a key column'}
                  />
                  <KPICard
                    title="Single-Source Rows" value={(health.singleSource || []).reduce((s, x) => s + x.rows, 0).toLocaleString()}
                    icon="🧍" color="#7c3aed" subtitle="never matched during a merge"
                  />
                </div>

                {(health.duplicates || []).length > 0 && (
                  <div>
                    <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.95rem', color: '#1e293b' }}>👯 Duplicate key values</h3>
                    <p style={{ margin: '0 0 0.6rem', fontSize: '0.76rem', color: '#94a3b8' }}>
                      Several rows share the same {columns.find((c) => c.column_key === health.keyColumn)?.label || 'key'} — merges will fill all of them. Click a value to filter the grid to it.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {health.duplicates.map((d) => (
                        <button
                          key={d.value}
                          style={{ ...BTN, padding: '0.2rem 0.6rem', fontSize: '0.78rem', borderColor: '#fecaca', color: '#b91c1c' }}
                          title="Show these rows in the grid"
                          onClick={() => { setColumnFilters({ [health.keyColumn]: [d.value] }); setView('grid'); }}
                        >
                          {d.value} × {d.count}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.95rem', color: '#1e293b' }}>🧍 Single-source rows</h3>
                  <p style={{ margin: '0 0 0.6rem', fontSize: '0.76rem', color: '#94a3b8' }}>
                    Rows only one file ever touched{(health.mergeSources || []).length ? ` — e.g. rows a merge (${health.mergeSources.join(', ')}) never matched` : ''}. Click to see them in the grid.
                  </p>
                  {(health.singleSource || []).length === 0 && <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>None — every row was matched by at least two sources.</div>}
                  {(health.singleSource || []).map((s) => {
                    const max = Math.max(...(health.singleSource || []).map((x) => x.rows), 1);
                    return (
                      <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5, cursor: 'pointer' }}
                        title="Show these rows in the grid"
                        onClick={() => { setColumnFilters({ __sources: [s.source] }); setView('grid'); }}>
                        <span style={{ width: 220, fontSize: '0.8rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.source}</span>
                        <div style={{ flex: 1, height: 14, backgroundColor: '#f1f5f9', borderRadius: 7, overflow: 'hidden' }}>
                          <div style={{ width: `${(s.rows / max) * 100}%`, height: '100%', backgroundColor: sourceColor(s.source), borderRadius: 7 }} />
                        </div>
                        <span style={{ width: 70, textAlign: 'right', fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>{s.rows.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>

                <div>
                  <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.95rem', color: '#1e293b' }}>🕳️ Emptiest columns</h3>
                  <p style={{ margin: '0 0 0.6rem', fontSize: '0.76rem', color: '#94a3b8' }}>Columns with the most blanks — normal for merged columns that only apply to some components, suspicious for key fields.</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {(() => {
                      const fillByKey = new Map((health.fills || []).map((f) => [f.column_key, f.filled]));
                      const totalR = health.totalRows || 1;
                      return columns
                        .filter((c) => !c.formula)
                        .map((c) => ({ c, pct: ((fillByKey.get(c.column_key) || 0) / totalR) * 100 }))
                        .sort((a, b) => a.pct - b.pct)
                        .slice(0, 15)
                        .map(({ c, pct }) => (
                          <div key={c.column_key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ width: 260, fontSize: '0.78rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.label}>{c.label}</span>
                            <div style={{ flex: 1, height: 10, backgroundColor: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct < 25 ? '#dc2626' : pct < 60 ? '#f59e0b' : '#16a34a', borderRadius: 5 }} />
                            </div>
                            <span style={{ width: 90, textAlign: 'right', fontSize: '0.76rem', color: '#64748b' }}>{pct.toFixed(0)}% filled</span>
                          </div>
                        ));
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {view === 'imports' && (
          <div style={{ padding: '1rem', overflowX: 'auto' }}>
            {/* Data-flow lineage: how each source feeds the master table */}
            {imports.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.2rem', fontSize: '1rem', color: '#1e293b' }}>🔀 How the master is fed</h3>
                <p style={{ margin: '0 0 0.8rem', fontSize: '0.78rem', color: '#94a3b8' }}>
                  Each source file flows into the master table either by appending new rows or merging into existing rows on a match key. Column chips show the columns each import created.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[...imports].reverse().map((im) => {
                    const fedCols = columns.filter((c) => c.source_import_id === im.id);
                    const matchLabel = im.match_key ? (columns.find((c) => c.column_key === im.match_key)?.label || im.match_key) : null;
                    return (
                      <div key={im.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem', border: '1px solid #f1f5f9', borderLeft: `4px solid ${sourceColor(im.label)}`, borderRadius: 8, padding: '0.55rem 0.8rem', backgroundColor: '#fcfcfd' }}>
                        <div style={{ minWidth: 195 }}>
                          <span style={{ fontSize: '0.74rem', color: 'white', backgroundColor: sourceColor(im.label), borderRadius: 10, padding: '0.14rem 0.55rem' }}>{im.label}</span>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4, maxWidth: 195, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={im.file_name || ''}>
                            {im.source_type.toUpperCase()}{im.file_name ? ` · ${im.file_name}` : ''}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#334155', paddingTop: 2, whiteSpace: 'nowrap' }}>
                          {im.mode === 'merge'
                            ? <>⇄ merged on <strong>{matchLabel}</strong>: {im.rows_updated.toLocaleString()} rows filled{im.rows_inserted > 0 ? `, ${im.rows_inserted.toLocaleString()} appended` : ''}</>
                            : <>↓ appended <strong>{im.rows_inserted.toLocaleString()}</strong> rows</>}
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', paddingTop: 2 }}>
                          {fedCols.length > 0 && (
                            <span style={{ fontSize: '0.72rem', color: '#64748b', marginRight: 2 }}>+{fedCols.length} col{fedCols.length > 1 ? 's' : ''}:</span>
                          )}
                          {fedCols.slice(0, 12).map((c) => (
                            <span key={c.column_key} style={{ fontSize: '0.68rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.08rem 0.45rem', color: '#475569' }} title={c.label}>
                              {c.label.length > 26 ? `${c.label.slice(0, 25)}…` : c.label}
                            </span>
                          ))}
                          {fedCols.length > 12 && (
                            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }} title={fedCols.slice(12).map((c) => c.label).join(', ')}>
                              +{fedCols.length - 12} more
                            </span>
                          )}
                          {fedCols.length === 0 && (
                            <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>fed existing (shared) columns only</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h3 style={{ margin: '0 0 0.6rem', fontSize: '1rem', color: '#1e293b' }}>📜 Import log</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date', 'Label', 'Type', 'Mode', 'File', 'Rows Added', 'Rows Merged', 'New Columns', 'By'].map((h) => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {imports.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8' }}>No imports yet.</td></tr>
                ) : imports.map((im) => (
                  <tr key={im.id}>
                    <td style={TD}>{new Date(im.created_at).toLocaleString()}</td>
                    <td style={TD}>
                      <span style={{ fontSize: '0.72rem', color: 'white', backgroundColor: sourceColor(im.label), borderRadius: 10, padding: '0.12rem 0.5rem' }}>{im.label}</span>
                    </td>
                    <td style={TD}>{im.source_type.toUpperCase()}</td>
                    <td style={TD}>{im.mode}{im.match_key ? ` (on ${columns.find((c) => c.column_key === im.match_key)?.label || im.match_key})` : ''}</td>
                    <td style={{ ...TD, maxWidth: 220 }} title={im.file_name}>{im.file_name || '—'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{im.rows_inserted}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{im.rows_updated}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{im.columns_added}</td>
                    <td style={TD}>{im.created_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filter dropdown */}
      {openFilter && (
        <FilterDropdown
          values={filterValues}
          selected={columnFilters[openFilter] || []}
          onToggle={(v) => toggleFilterValue(openFilter, v)}
          onClear={() => { setColumnFilters((p) => { const n = { ...p }; delete n[openFilter]; return n; }); setOpenFilter(null); }}
          onClose={() => setOpenFilter(null)}
          position={filterPos}
          loading={filterLoading}
        />
      )}

      {/* Import wizard */}
      <ImportWizard
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        jobNumber={jobNumber}
        listId={listId}
        columns={columns}
        apiCall={apiCall}
        onImported={(msg) => { showToast(msg); loadAll(); loadLists(); }}
      />

      {/* Column manager */}
      <ColumnManager
        isOpen={showColumns}
        onClose={() => setShowColumns(false)}
        columns={columns}
        hiddenCols={hiddenCols}
        onToggleHidden={cmToggleHidden}
        onSetHiddenMany={cmSetHiddenMany}
        onRename={cmRename}
        onReorder={cmReorder}
        onDelete={cmDelete}
        onAdd={cmAdd}
        pinnedCols={pinnedCols}
        onTogglePin={togglePin}
      />

      {/* Masters manager */}
      <ManageListsModal
        isOpen={showLists}
        onClose={() => setShowLists(false)}
        lists={lists}
        activeListId={listId}
        onCreate={listCreate}
        onRename={listRename}
        onDelete={listDelete}
        onSwitch={switchList}
      />

      {/* Saved views */}
      <ViewsModal
        isOpen={showViews}
        onClose={() => setShowViews(false)}
        views={views}
        onApply={applyView}
        onSave={saveView}
        onDelete={deleteView}
        hasState={!!(search || activeFilterCount || sortConfig.key || hiddenCols.size || pinnedCols.length)}
      />

      {/* Add row */}
      <Modal isOpen={showAddRow} onClose={() => setShowAddRow(false)} title="➕ Add Row" width="600px">
        {columns.length === 0 ? (
          <p style={{ color: '#64748b' }}>Create columns first (via Import or the Columns manager).</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem', maxHeight: '55vh', overflowY: 'auto', paddingRight: 4 }}>
              {columns.filter((c) => !hiddenCols.has(c.column_key) && !c.formula).map((c) => (
                <div key={c.column_key}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>{c.label}</label>
                  <input
                    value={addRowValues[c.column_key] || ''}
                    onChange={(e) => setAddRowValues((p) => ({ ...p, [c.column_key]: e.target.value }))}
                    style={{ ...INPUT, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button style={BTN} onClick={() => setShowAddRow(false)}>Cancel</button>
              <button style={BTN_PRIMARY} onClick={handleAddRow}>Save Row</button>
            </div>
          </>
        )}
      </Modal>

      {/* Help */}
      <Modal
        isOpen={showHelp} onClose={() => setShowHelp(false)} title="❓ Master Component List — Help" width="900px"
        headerExtra={<ListenButton compact guideId="master-component-list" title="Master Component List Help" accent="#0f766e" dark />}
      >
        <HelpWithVideoTour guideId="master-component-list" title="Master Component List Help" accent="#0f766e">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {/* One-liner */}
          <div style={{ background: 'linear-gradient(135deg, #f0fdfa, #ecfeff)', border: '1px solid #99f6e4', borderRadius: 10, padding: '0.8rem 1rem', fontSize: '0.9rem', color: '#134e4a' }}>
            <strong>One table for the entire project scope.</strong> Every activity, spool, steel tag, module, equipment and hour — pulled from schedules and estimate sheets into one place, so nothing lives only in somebody's spreadsheet.
          </div>

          {/* Visual data flow */}
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>How data gets in</div>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 170px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[['📄', 'XER schedules', '#1d4ed8'], ['📊', 'Excel sheets (MTO, details…)', '#b45309'], ['🖊', 'Manual edits & rows', '#7c3aed']].map(([ic, lbl, clr]) => (
                  <div key={lbl} style={{ border: `1.5px solid ${clr}22`, borderLeft: `4px solid ${clr}`, borderRadius: 8, padding: '0.45rem 0.6rem', fontSize: '0.78rem', color: '#334155', backgroundColor: '#fcfcfd', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontSize: '1rem' }}>{ic}</span>{lbl}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 0.6rem', color: '#94a3b8', fontSize: '1.3rem' }}>➜</div>
              <div style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
                <div style={{ border: '1.5px dashed #0f766e', borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.78rem', color: '#0f766e', backgroundColor: '#f0fdfa' }}>
                  <strong>Append</strong> — every row lands as a new row
                </div>
                <div style={{ border: '1.5px dashed #0f766e', borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.78rem', color: '#0f766e', backgroundColor: '#f0fdfa' }}>
                  <strong>Merge</strong> — fills columns into existing rows on a match key (Tag, SOV, Activity ID…) like Power Query
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 0.6rem', color: '#94a3b8', fontSize: '1.3rem' }}>➜</div>
              <div style={{ flex: '1 1 190px', display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '100%', background: 'linear-gradient(135deg, #0f172a, #134e4a)', borderRadius: 10, padding: '0.7rem 0.8rem', color: 'white' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 5 }}>🧩 Master table</div>
                  <div style={{ fontSize: '0.7rem', color: '#94e2d5', marginBottom: 6 }}>fluid columns, no limit — each row keeps its sources:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {['XER 20260719', 'SM Take-off', 'Manual'].map((s) => (
                      <span key={s} style={{ fontSize: '0.62rem', color: 'white', backgroundColor: sourceColor(s), borderRadius: 8, padding: '0.08rem 0.4rem' }}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reading the grid */}
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Reading the grid</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', fontSize: '0.74rem' }}>
              <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ flex: 1, padding: '0.3rem 0.6rem', color: sourceColor('XER 20260719'), borderLeft: `3px solid ${sourceColor('XER 20260719')}`, backgroundColor: '#f8fafc' }}>XER 20260719</div>
                <div style={{ flex: 1, padding: '0.3rem 0.6rem', color: sourceColor('GT Pipe'), borderLeft: `3px solid ${sourceColor('GT Pipe')}`, backgroundColor: '#f8fafc' }}>GT Pipe</div>
                <div style={{ flex: 1, padding: '0.3rem 0.6rem', color: sourceColor('SM Take-off'), borderLeft: `3px solid ${sourceColor('SM Take-off')}`, backgroundColor: '#f8fafc' }}>SM Take-off</div>
              </div>
              <div style={{ display: 'flex', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>
                <div style={{ flex: 1, padding: '0.3rem 0.6rem' }}>Activity ID ▲ 🔽</div>
                <div style={{ flex: 1, padding: '0.3rem 0.6rem' }}>Spool 🔽</div>
                <div style={{ flex: 1, padding: '0.3rem 0.6rem' }}>Total Hours 🔽</div>
              </div>
              <div style={{ padding: '0.45rem 0.6rem', color: '#64748b' }}>
                The <strong>colored top row</strong> tells you which import each column came from. <strong>Click a header</strong> to sort, use the <strong>funnel</strong> to filter by values, hit <strong>📌</strong> to freeze a key column on the left, and <strong>click any cell</strong> to edit it in place. Purple 🧮 columns are computed live and can't be edited.
              </div>
            </div>
          </div>

          {/* Feature cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
            {[
              ['📥', 'Import', 'XER or Excel via the wizard: pick the sheet, header row and columns, then Append or Merge. New columns appear automatically.', '#0f766e'],
              ['🗂️', 'Sources never lie', 'Chips on every row list each file (and manual edit) that touched it. Filter by chip to see one source\u2019s rows.', '#1d4ed8'],
              ['🗃️', 'Multiple masters', 'The dropdown next to the title switches between independent masters — separate scopes, phases or what-if snapshots, each with its own columns and rows.', '#0e7490'],
              ['🔖', 'Saved views', 'Save your search + filters + sort + hidden and pinned columns as a named view. Share it (🌐) so the whole team opens the same slice.', '#be185d'],
              ['📌', 'Pinned columns', 'Pin up to four key columns (Tag, Activity ID…) — they stay frozen on the left while you scroll across the wide table.', '#b45309'],
              ['🩺', 'Health tab', 'One click finds duplicate tags, rows a merge never matched, blanks in your key column and the emptiest columns.', '#dc2626'],
              ['🧮', 'Computed columns', 'Add live delta columns — hours between two snapshots, schedule slip in days (finish − finish), ratios. Analytics shows the biggest movers.', '#7c3aed'],
              ['📊', 'Columns manager', 'Filter by name or source, multi-select source chips, bulk show/hide, pin, rename (click), reorder, delete.', '#b45309'],
              ['🔀', 'Data Flow tab', 'Lineage cards showing every import: file → append/merge on which key → rows filled → columns created, plus the full log.', '#7c3aed'],
              ['🔍', 'Find anything', 'The search box scans every column at once; funnels and 3-state sorting narrow it down.', '#be185d'],
              ['🔎', 'Jump to tracker', 'Select one row and hit 🔎 Tracker — the key value is copied and the progress tracker opens in a new tab, ready to paste.', '#166534'],
              ['📤', 'Export', 'One click gives the whole table in Excel — all columns (computed ones too), sources and audit stamps included.', '#166534'],
            ].map(([ic, title, desc, clr]) => (
              <div key={title} style={{ border: '1px solid #f1f5f9', borderTop: `3px solid ${clr}`, borderRadius: 10, padding: '0.6rem 0.75rem', backgroundColor: '#fcfcfd' }}>
                <div style={{ fontWeight: 700, color: '#1f2937', fontSize: '0.85rem', marginBottom: 3 }}>{ic} {title}</div>
                <div style={{ color: '#6b7280', fontSize: '0.78rem', lineHeight: 1.45 }}>{desc}</div>
              </div>
            ))}
          </div>

          {/* Routine */}
          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '0.7rem 0.9rem', fontSize: '0.8rem', color: '#78350f' }}>
            💡 <strong>A good rhythm:</strong> new schedule lands → import the XER merged on Activity ID so snapshots sit side by side, then open <strong>🩺 Health</strong> to catch duplicates and rows the merge missed while it's fresh. Estimate sheet frozen → import it merged on Tag or SOV. Add a <strong>🧮 computed column</strong> between two snapshots to see slips, save the setup as a <strong>🔖 shared view</strong>, and ask the table real questions — e.g. filter to components with hours but no drawing number.
          </div>
        </div>
        </HelpWithVideoTour>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default MasterComponentList;
